// The gated half of the teacher page: the two doors of the offer, and the gate
// in front of them.
//
// TWO REQUIREMENTS, TOGETHER (plan §3a). The caller must hold a copy of the
// translation AND fill the form. The purchase is the qualification; the form is
// the intent. Neither on its own is a lead, and neither on its own should reach
// a purchasing officer's inbox.
//
// THE GATE EXPLAINS ITSELF. A disabled control with no reason is the worst
// version of this, so each state says what is missing, why it is asked for, and
// what it costs — and the offer itself sits above, unconditionally, so nobody
// has to buy anything to find out what they would be buying into.
//
// The server is the enforcement (`convex/classroom.ts` re-checks entitlement on
// every submit); this is the explanation.

import { useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { Check, Copy, Loader2, LockKeyhole } from "lucide-react";
import { toast } from "sonner";
import { api } from "@bindery/convex/_generated/api";
import { Button } from "@bindery/ui/components/ui/button";
import { Input } from "@bindery/ui/components/ui/input";
import { Label } from "@bindery/ui/components/ui/label";
import { Skeleton } from "@bindery/ui/components/ui/skeleton";
import { Textarea } from "@bindery/ui/components/ui/textarea";
import { signIn } from "@/lib/auth";
import { errorMessage } from "@/lib/convex-error";
import { useLogger, useLoggerContext } from "@/logging";
import { checkoutAttribution } from "@/lib/attribution";
import { useCoarseNow } from "@/lib/use-coarse-now";
import { useBuyProduct } from "@/reader/use-buy-product";

/** Which door the teacher is filling in. `null` closes both forms. */
type OpenDoor = "purchaser_link" | "purchaser_contact" | null;

/** The heading every state of this block wears, so the page's rhythm holds
 *  whether the reader is signed out, unentitled, or filling the form. */
function ActionsFrame({
  children,
  note,
}: {
  children: React.ReactNode;
  note?: string;
}) {
  return (
    <section className="mt-10">
      <h2 className="text-muted-foreground text-center font-serif text-[0.75rem] tracking-[0.35em] uppercase">
        Ask for your class
      </h2>
      {note !== undefined && (
        <p className="text-muted-foreground mx-auto mt-4 max-w-prose text-center font-serif text-[1rem] leading-relaxed">
          {note}
        </p>
      )}
      <div className="mt-6">{children}</div>
    </section>
  );
}

/** The gate's standing explanation — the same two requirements in every state,
 *  so the reason never changes shape as the reader's status does. */
function Requirements() {
  return (
    <ol className="text-muted-foreground mt-5 space-y-3 font-serif text-[1rem] leading-relaxed">
      <li className="flex gap-3">
        <span className="font-rockwell text-foreground">1.</span>
        <span>
          <span className="text-foreground">A copy of your own.</span> $8 for a
          volume — the qualification, not the price of the offer. Your students
          still pay nothing.
        </span>
      </li>
      <li className="flex gap-3">
        <span className="font-rockwell text-foreground">2.</span>
        <span>
          <span className="text-foreground">A word about your class.</span> Who
          you are, where you teach, and roughly how many students. We do not
          approach anyone on the strength of a purchase alone.
        </span>
      </li>
    </ol>
  );
}

/** Signed out, or signed in without a copy. Same frame, different offer. */
function Gated({ authed }: { authed: boolean }) {
  const { buy, busy } = useBuyProduct("/for-teachers/odyssey");
  return (
    <ActionsFrame>
      <div className="border border-[--rule] px-6 py-6">
        <p className="text-foreground flex items-center gap-3 font-serif text-[1.05rem]">
          <LockKeyhole aria-hidden="true" className="size-4 shrink-0" />
          Two things before we write to your district.
        </p>
        <Requirements />
        <div className="mt-6 flex flex-wrap items-center gap-3">
          {authed ? (
            <Button
              onClick={() => {
                void buy("odyssey-vol2-premium");
              }}
              disabled={busy}
            >
              {busy && (
                <Loader2 aria-hidden="true" className="size-4 animate-spin" />
              )}
              Buy volume two — $8
            </Button>
          ) : (
            <Button
              onClick={() => {
                signIn();
              }}
            >
              Sign in
            </Button>
          )}
          <p className="text-muted-foreground font-serif text-sm italic">
            {authed
              ? "Already bought a volume? It may take a moment to appear."
              : "You can read Books 1–5, 9, 13 and 21 without signing in."}
          </p>
        </div>
      </div>
    </ActionsFrame>
  );
}

/** One field, in the page's face rather than the console's. */
function Field({
  id,
  label,
  hint,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-foreground font-serif">
        {label}
      </Label>
      {children}
      {hint !== undefined && (
        <p className="text-muted-foreground font-serif text-sm italic">
          {hint}
        </p>
      )}
    </div>
  );
}

/** The purchaser link, once it exists: the thing the teacher actually forwards.
 *  Shown with the URL in full, because a link someone is about to paste into an
 *  email to their director needs to be legible before it is copied. */
function PurchaserLink({ token }: { token: string }) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/classroom/${token}`;
  return (
    <div className="mt-6 border border-[--rule] px-6 py-5">
      <p className="text-muted-foreground font-serif text-sm tracking-[0.2em] uppercase">
        Your link to forward
      </p>
      <p className="text-foreground mt-3 font-mono text-sm break-all">{url}</p>
      <Button
        variant="outline"
        className="mt-4"
        onClick={() => {
          void navigator.clipboard
            .writeText(url)
            .then(() => {
              setCopied(true);
              toast.success("Copied. Send it to whoever signs the orders.");
            })
            .catch(() => {
              toast.error("Couldn't copy — select the link and copy it.");
            });
        }}
      >
        {copied ? (
          <Check aria-hidden="true" className="size-4" />
        ) : (
          <Copy aria-hidden="true" className="size-4" />
        )}
        {copied ? "Copied" : "Copy link"}
      </Button>
      <p className="text-muted-foreground mt-4 font-serif text-[0.95rem] leading-relaxed">
        It opens a page addressed to them, saying what you asked for and what it
        would cost the district. Nothing on it asks them to pay anything today.
      </p>
    </div>
  );
}

/** Both doors, for a teacher who qualifies. */
function Doors({ existingLinkToken }: { existingLinkToken: string | null }) {
  const submit = useMutation(api.classroom.submitInquiry);
  const log = useLogger();
  const { leadId } = useLoggerContext();
  const now = useCoarseNow();
  const [open, setOpen] = useState<OpenDoor>(null);
  const [busy, setBusy] = useState(false);
  const [token, setToken] = useState<string | null>(existingLinkToken);
  const [sent, setSent] = useState(false);

  const onSubmit = async (
    kind: Exclude<OpenDoor, null>,
    form: HTMLFormElement,
  ): Promise<void> => {
    const data = new FormData(form);
    // `FormData.get` is `string | File | null`, and `String(File)` is the
    // useless "[object File]" — a text field is not the only thing that can
    // carry a form entry's name.
    const text = (name: string): string => {
      const value = data.get(name);
      return typeof value === "string" ? value : "";
    };
    const students = Number(data.get("students"));
    setBusy(true);
    try {
      const result = await submit({
        kind,
        teacherName: text("teacherName"),
        school: text("school"),
        role: text("role"),
        students:
          Number.isFinite(students) && students > 0 ? students : undefined,
        message: text("message"),
        purchaserName: text("purchaserName"),
        purchaserEmail: text("purchaserEmail"),
        now,
        // The ad click that led here, read out of our own storage at the
        // moment of intent — the inquiry is the classroom conversion, and the
        // click id is what its upload joins on. The server re-validates and
        // drops what it doesn't recognize (`convex/model/attribution.ts`).
        attribution: checkoutAttribution(leadId),
      });
      // The KIND only — the form's contents are PII and a wide event is not
      // where they go (logging-strategy).
      log.event("classroom.inquiry_submitted", { kind });
      if (kind === "purchaser_link") {
        setToken(result.token);
        setOpen(null);
      } else {
        setSent(true);
        setOpen(null);
        toast.success("Thank you — we will make the approach this week.");
      }
    } catch (error) {
      toast.error("That didn't go through", {
        description: errorMessage(error, "Please try again."),
      });
    } finally {
      setBusy(false);
    }
  };

  const commonFields = (
    <>
      <Field id="teacherName" label="Your name">
        <Input id="teacherName" name="teacherName" required maxLength={120} />
      </Field>
      <Field id="school" label="School">
        <Input id="school" name="school" required maxLength={200} />
      </Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field id="role" label="What you teach">
          <Input
            id="role"
            name="role"
            maxLength={120}
            placeholder="9th grade English"
          />
        </Field>
        <Field id="students" label="How many students">
          <Input
            id="students"
            name="students"
            type="number"
            min={1}
            max={500}
          />
        </Field>
      </div>
    </>
  );

  return (
    <ActionsFrame note="Two ways to do this. Either is fine, and the second is faster.">
      {token !== null && <PurchaserLink token={token} />}
      {sent && (
        <p className="text-foreground mt-6 border border-[--rule] px-6 py-5 font-serif text-[1.05rem] leading-relaxed">
          We have your purchaser’s details and will write to them. If you would
          rather send something yourself in the meantime, you can still take a
          link below.
        </p>
      )}

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <button
          type="button"
          onClick={() => {
            setOpen(open === "purchaser_link" ? null : "purchaser_link");
          }}
          className="hover:border-foreground border border-[--rule] px-5 py-5 text-left transition-colors"
        >
          <span className="font-display text-foreground text-lg font-black tracking-tight">
            Give me a link
          </span>
          <span className="text-muted-foreground mt-2 block font-serif text-[0.95rem] leading-relaxed">
            You forward it to whoever makes the purchasing decision.
          </span>
        </button>
        <button
          type="button"
          onClick={() => {
            setOpen(open === "purchaser_contact" ? null : "purchaser_contact");
          }}
          className="hover:border-foreground border border-[--rule] px-5 py-5 text-left transition-colors"
        >
          <span className="font-display text-foreground text-lg font-black tracking-tight">
            You write to them
          </span>
          <span className="text-muted-foreground mt-2 block font-serif text-[0.95rem] leading-relaxed">
            Give us their name and email and we will make the approach.
          </span>
        </button>
      </div>

      {open !== null && (
        <form
          className="mt-6 space-y-4 border border-[--rule] px-6 py-6"
          onSubmit={(event) => {
            event.preventDefault();
            void onSubmit(open, event.currentTarget);
          }}
        >
          {commonFields}
          {open === "purchaser_contact" && (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field id="purchaserName" label="Their name">
                <Input
                  id="purchaserName"
                  name="purchaserName"
                  required
                  maxLength={120}
                />
              </Field>
              <Field id="purchaserEmail" label="Their email">
                <Input
                  id="purchaserEmail"
                  name="purchaserEmail"
                  type="email"
                  required
                  maxLength={254}
                />
              </Field>
            </div>
          )}
          <Field
            id="message"
            label="Anything else"
            hint="Optional. When your unit runs, what your students are like, what your district usually needs to see."
          >
            <Textarea id="message" name="message" rows={3} maxLength={2000} />
          </Field>
          <Button type="submit" disabled={busy}>
            {busy && (
              <Loader2 aria-hidden="true" className="size-4 animate-spin" />
            )}
            {open === "purchaser_link" ? "Make my link" : "Send it to us"}
          </Button>
        </form>
      )}
    </ActionsFrame>
  );
}

export function ClassroomActions() {
  const now = useCoarseNow();
  const status = useQuery(api.classroom.myStatus, { now });

  if (status === undefined) {
    return (
      <ActionsFrame>
        <Skeleton className="h-40 w-full" />
      </ActionsFrame>
    );
  }
  if (!status.entitled) return <Gated authed={status.authed} />;

  const existingLink =
    status.inquiries.find((row) => row.kind === "purchaser_link")?.token ??
    null;
  return <Doors existingLinkToken={existingLink} />;
}
